import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable } from 'mobx';

import { StoryManagerViewStore } from './StoryManagerViewStore';
import { UserStoryUploadFlowStore } from './UserStoryUploadFlowStore';

import type { RootStore } from './RootStore';
import type { DashboardStory, StoryDTO } from '@src/types.ts/story';

const logger = LogManager.getLogger('store:DashboardStore');

export class DashboardStore {
  userStoryUploadFlowStore: UserStoryUploadFlowStore;
  // Ephemeral Story Manager view state (selection, expansion, sort, tree panel,
  // scroll) — lives here so it survives navigating away and back.
  storyManagerViewStore: StoryManagerViewStore;
  allStories: DashboardStory[] = [];
  // Album the current `allStories` were loaded for. Lets Story Manager tell an
  // already-loaded album (render cached list, no spinner) from an album change
  // (full reload). Mirrors the `homeStoriesAlbumId` cache below.
  allStoriesAlbumId: string | null = null;

  // Home page cache
  homeStories: DashboardStory[] = [];
  homeStoriesAlbumId: string | null = null;
  homeStoriesLoading = true;

  // Progressive population tracking
  populatedStoryIds: Set<string> = new Set();

  constructor(private rootStore: RootStore) {
    makeAutoObservable(
      this,
      {
        userStoryUploadFlowStore: false,
        storyManagerViewStore: false,
        populatedStoryIds: observable.ref,
      },
      { autoBind: true },
    );
    this.userStoryUploadFlowStore = new UserStoryUploadFlowStore(this);
    this.storyManagerViewStore = new StoryManagerViewStore();
  }

  private get storyRepository() {
    return this.rootStore.datoClientStore.storyRepository;
  }

  setAllStories(stories: DashboardStory[], albumId?: string) {
    this.allStories = stories;
    if (albumId !== undefined) {
      this.allStoriesAlbumId = albumId;
    }
  }

  addStory(story: DashboardStory) {
    const existingIndex = this.allStories.findIndex(s => s.id === story.id);
    if (existingIndex >= 0) {
      this.updateStory(story);
      return;
    }
    this.setAllStories([story, ...this.allStories]);
  }

  removeStories(storyIds: string[]) {
    const idSet = new Set(storyIds);
    this.setAllStories(this.allStories.filter(s => !idSet.has(s.id)));
  }

  updateStory(story: DashboardStory) {
    this.setAllStories(
      this.allStories.map(item => (item.id === story.id ? story : item)),
    );
  }

  saveStory(story: Partial<StoryDTO> & { id: string }) {
    return this.storyRepository.update(story);
  }

  // Deferred load of story-level shared contents (Story Manager's platform /
  // analytics columns). Fired after the story list paints — the inverse
  // -reference lookups are too expensive to sit in the album query's critical
  // path. Merges into `allStories` unless the album changed mid-flight.
  async loadSharedContentsForAllStories(albumId: string) {
    if (!this.storyRepository) return;
    try {
      const byStoryId = await this.storyRepository.findManySharedContents(
        albumId,
        {
          includeDrafts: true,
          excludeInvalid: false,
          environment: this.rootStore.userIdentityStore.environment,
        },
      );
      if (this.allStoriesAlbumId !== albumId) return;
      this.setAllStories(
        this.allStories.map(story =>
          byStoryId[story.id]
            ? { ...story, _allReferencingSharedContents: byStoryId[story.id] }
            : story,
        ),
      );
    } catch (error) {
      logger.warn('Failed to load shared contents for album', {
        albumId,
        error: extractErrorInfo(error),
      });
    }
  }

  // Home page cache actions
  setHomeStories(stories: DashboardStory[], albumId: string) {
    this.homeStories = stories;
    this.homeStoriesAlbumId = albumId;
  }

  setHomeStoriesLoading(loading: boolean) {
    this.homeStoriesLoading = loading;
  }

  clearHomeCache() {
    this.homeStories = [];
    this.homeStoriesAlbumId = null;
  }

  // Progressive population actions
  markStoriesPopulated(storyIds: string[]) {
    const newSet = new Set(this.populatedStoryIds);
    storyIds.forEach(id => newSet.add(id));
    this.populatedStoryIds = newSet;
  }

  isStoryPopulated(storyId: string): boolean {
    return this.populatedStoryIds.has(storyId);
  }

  clearPopulationState() {
    this.populatedStoryIds = new Set();
  }
}
